import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Search, Play, Pause, Plus, Check, Music2, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { useLang } from '../context/LangContext';
import { useProject } from '../context/ProjectContext';
import { musicTracks, musicI18n } from '../mock/data';
import { toast } from '../hooks/use-toast';

const MusicLibrary = () => {
  const { lang } = useLang();
  const t = musicI18n[lang];
  const { tracks: added, addTrack, removeTrack } = useProject();

  const [query, setQuery] = useState('');
  const [genre, setGenre] = useState(t.genres[0]);
  const [mood, setMood] = useState(t.moods[0]);
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState({});
  const audioRef = useRef(null);

  // Reset genre/mood on lang change
  useEffect(() => { setGenre(musicI18n[lang].genres[0]); setMood(musicI18n[lang].moods[0]); }, [lang]);

  const genreMap = { 'Alla': null, 'الكل': null, 'Electronic': 'Electronic', 'إلكترونية': 'Electronic', 'Lo-Fi': 'Lo-Fi', 'لو-فاي': 'Lo-Fi', 'Pop': 'Pop', 'بوب': 'Pop', 'Hip-Hop': 'Hip-Hop', 'هيب-هوب': 'Hip-Hop', 'Jazz': 'Jazz', 'جاز': 'Jazz', 'Ambient': 'Ambient', 'محيطية': 'Ambient', 'Cinematic': 'Cinematic', 'سينمائية': 'Cinematic', 'Synthwave': 'Synthwave', 'سينثويف': 'Synthwave' };
  const moodMap = { 'Alla stämningar': null, 'جميع الأجواء': null, 'Energisk': 'Energisk', 'حماسية': 'Energisk', 'Chill': 'Chill', 'هادئة': 'Chill', 'Episk': 'Episk', 'ملحمية': 'Episk', 'Lugn': 'Lugn', 'سكينة': 'Lugn', 'Trendig': 'Trendig', 'رائجة': 'Trendig', 'Glad': 'Glad', 'مبهجة': 'Glad', 'Mysig': 'Mysig', 'دافئة': 'Mysig', 'Dramatisk': 'Dramatisk', 'درامية': 'Dramatisk' };

  const filtered = useMemo(() => {
    const g = genreMap[genre];
    const m = moodMap[mood];
    const q = query.trim().toLowerCase();
    return musicTracks.filter(tr => {
      if (g && tr.genre !== g) return false;
      if (m && tr.mood !== m) return false;
      if (q && !`${tr.title} ${tr.artist} ${tr.genre} ${tr.mood}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [query, genre, mood]);

  const play = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(track.url);
    audio.volume = 0.7;
    audio.play().catch(() => {});
    audio.addEventListener('timeupdate', () => {
      setProgress(p => ({ ...p, [track.id]: audio.duration ? (audio.currentTime / audio.duration) * 100 : 0 }));
    });
    audio.addEventListener('ended', () => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(track.id);
  };

  const toggleAdd = (track) => {
    const isAdded = added.find(x => x.id === track.id);
    if (isAdded) {
      removeTrack(track.id);
      toast({ title: lang === 'sv' ? 'Borttaget' : 'تمت الإزالة', description: track.title });
    } else {
      addTrack({ id: track.id, title: track.title, artist: track.artist });
      toast({ title: t.added, description: `${track.title} — ${track.artist}` });
    }
  };

  return (
    <section className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-10">
          <span className="inline-block text-[13px] font-bold tracking-widest uppercase text-emerald-600 mb-4">{t.sectionLabel}</span>
          <h2 className="text-[40px] md:text-[56px] font-black tracking-[-0.02em] leading-[1.05] text-neutral-900">{t.title}</h2>
          <p className="mt-4 text-[16px] text-neutral-600">{t.subtitle}</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-4 md:p-5 mb-6 shadow-sm">
          <div className="grid md:grid-cols-[minmax(0,1fr)_auto_auto] gap-3">
            <div className="relative">
              <Search className="absolute left-4 rtl:left-auto rtl:right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.search}
                className="pl-11 rtl:pl-4 rtl:pr-11 h-12 rounded-xl bg-neutral-50 border-neutral-200 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-0"
              />
            </div>
            <select value={genre} onChange={(e) => setGenre(e.target.value)} className="h-12 px-4 rounded-xl bg-neutral-50 border border-neutral-200 text-[14px] font-semibold text-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-500">
              {t.genres.map(g => <option key={g}>{g}</option>)}
            </select>
            <select value={mood} onChange={(e) => setMood(e.target.value)} className="h-12 px-4 rounded-xl bg-neutral-50 border border-neutral-200 text-[14px] font-semibold text-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-500">
              {t.moods.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          {added.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-[13px] text-neutral-600">
              <Music2 className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold text-neutral-900">{added.length}</span> {t.projectCount}
            </div>
          )}
        </div>

        {/* Tracks */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-neutral-500">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-neutral-300" />
            <p className="text-[14.5px]">{t.noResults}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map(track => {
              const isPlaying = playingId === track.id;
              const isAdded = !!added.find(x => x.id === track.id);
              const pr = progress[track.id] || 0;
              return (
                <div key={track.id} className={`group bg-white rounded-2xl border p-3 md:p-4 flex items-center gap-4 transition-all ${isPlaying ? 'border-emerald-400 shadow-md' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm'}`}>
                  <button onClick={() => play(track)} className={`relative flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${track.color} flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                    {isPlaying ? <Pause className="w-5 h-5 fill-white text-white" /> : <Play className="w-5 h-5 fill-white text-white ml-0.5" />}
                    {isPlaying && (
                      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="3" strokeDasharray={`${pr * 2.89} 289`} strokeLinecap="round" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[15px] text-neutral-900 truncate">{track.title}</div>
                    <div className="text-[12.5px] text-neutral-500 truncate">{track.artist}</div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10.5px] font-bold bg-neutral-100 text-neutral-700 rounded-full px-2 py-0.5">{track.genre}</span>
                      <span className="text-[10.5px] font-bold bg-neutral-100 text-neutral-700 rounded-full px-2 py-0.5">{track.mood}</span>
                      <span className="text-[10.5px] font-bold bg-neutral-100 text-neutral-700 rounded-full px-2 py-0.5">{track.bpm} {t.bpm}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-2">
                    <span className="text-[12px] font-semibold text-neutral-500 tabular-nums">{track.duration}</span>
                    <button onClick={() => toggleAdd(track)} className={`h-9 px-3 rounded-full text-[12.5px] font-bold flex items-center gap-1.5 transition-colors ${isAdded ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-neutral-900 text-white hover:bg-neutral-700'}`}>
                      {isAdded ? <><Check className="w-3.5 h-3.5" /> {lang === 'sv' ? 'Tillagd' : 'مضاف'}</> : <><Plus className="w-3.5 h-3.5" /> {t.add}</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default MusicLibrary;
